import { getAcceptedLanguageCode } from "@/utils/config-utils";
import {
  Bell, Button, CheckCircle, Input, Lock,
  Save,
  Settings as SettingsIcon
} from "@ews-admin/global-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ChangePinSection } from "../../components/molecules/ChangePin/ChangePinSection";
import { PinLockDelaySection } from "../../components/molecules/PinLockDelay/PinLockDelaySection";
import {
  NotificationsSchema,
  useNotificationsSchema,
} from "../../schemas/settings.schema";
import { userApi } from "../../services/user.api";
import { setError } from "../../store/reducers/errorSlice";
import {
  getSessionSuccess,
  selectSessionAuthState,
} from "../../store/reducers/sessionSlice";
import { errorCode, errorType } from "../../types/error.definition";
import {
  NotificationEnum,
  PasswordForm,
  UserWithSettings,
} from "../../types/settings.definition";

export const UserSettings: React.FC = () => {
  const { i18n } = useTranslation();
  const appLanguage = i18n.language;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { session } = useSelector(selectSessionAuthState);
  const [activeTab, setActiveTab] = useState<"password" | "notifications">(
    "password",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [animateCards, setAnimateCards] = useState(false);

  const [passwordForm, setPasswordForm] = useState<PasswordForm>({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Track which fields have been interacted with to show inline errors
  const [passwordTouched, setPasswordTouched] = useState<
    Record<keyof PasswordForm, boolean>
  >({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  // React Hook Form setup for notifications
  const notificationsSchema = useNotificationsSchema();
  const {
    control,
    formState: { isDirty },
    handleSubmit: handleNotificationsFormSubmit,
    reset,
  } = useForm<NotificationsSchema>({
    resolver: zodResolver(notificationsSchema),
    mode: "onChange",
    defaultValues: {
      notifications: (() => {
        const user = session?.user as UserWithSettings;
        const userSettings = user?.settings?.notifications;
        return (
          userSettings || [
            NotificationEnum.APPOINTMENTS,
            NotificationEnum.REMINDERS,
          ]
        );
      })(),
    },
  });

  // Update notification form when session loads
  useEffect(() => {
    const user = session?.user as UserWithSettings;
    const userSettings = user?.settings?.notifications;
    if (userSettings) {
      reset({
        notifications: userSettings,
      });
    }
  }, [session, reset]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateCards(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Derived: password form is valid when all fields satisfy rules
  const isPasswordFormValid =
    passwordForm.oldPassword.length > 0 &&
    passwordForm.newPassword.length >= 8 &&
    passwordForm.confirmPassword === passwordForm.newPassword;

  // Derived: per-field inline error messages (only shown after field is touched)
  const passwordErrors = {
    oldPassword:
      passwordTouched.oldPassword && !passwordForm.oldPassword
        ? t("userSettings.messages.currentPasswordRequired")
        : "",
    newPassword:
      passwordTouched.newPassword &&
        passwordForm.newPassword.length > 0 &&
        passwordForm.newPassword.length < 8
        ? t("userSettings.messages.passwordTooShort")
        : "",
    confirmPassword:
      passwordTouched.confirmPassword &&
        passwordForm.confirmPassword.length > 0 &&
        passwordForm.confirmPassword !== passwordForm.newPassword
        ? t("userSettings.messages.passwordMismatch")
        : "",
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPasswordTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched to surface any remaining errors
    setPasswordTouched({
      oldPassword: true,
      newPassword: true,
      confirmPassword: true,
    });

    if (!isPasswordFormValid) return;

    setIsLoading(true);
    setSuccessMessage("");

    try {
      await userApi.changePassword({
        oldPassword: passwordForm.oldPassword,
        newPassword: passwordForm.newPassword,
      });

      setSuccessMessage(t("userSettings.messages.passwordUpdated"));
      setPasswordForm({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordTouched({
        oldPassword: false,
        newPassword: false,
        confirmPassword: false,
      });
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      // Error is automatically dispatched by API interceptor
      console.error("Password change failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationsSubmit = async (formValues: NotificationsSchema) => {
    setIsLoading(true);
    setSuccessMessage("");

    try {
      if (!session?.user?.id) {
        dispatch(
          setError({
            code: errorCode.USER_NOT_FOUND,
            category: errorType.CLIENT_FIELDS_ERROR,
            message: t("userSettings.messages.userNotFound"),
          }),
        );
        setIsLoading(false);
        return;
      }

      const user = session?.user as UserWithSettings;
      const payload = {
        settingsId: user?.settings?.settingsId,
        theme: user?.settings?.theme,
        timeZone: user?.settings?.timeZone,
        visibility: user?.settings?.visibility,
        appLanguage:
          user?.settings?.appLanguage || getAcceptedLanguageCode(appLanguage),
        notifications: formValues.notifications,
        userId: user?.settings?.userId,
      };
      await userApi.updateUserSettings(session.user.id, payload);

      // Update Redux session so the saved values persist across navigation
      const updatedUser = session.user as UserWithSettings;
      dispatch(
        getSessionSuccess({
          ...session,
          user: {
            ...updatedUser,
            settings: {
              ...updatedUser.settings,
              notifications: formValues.notifications,
            },
          } as UserWithSettings,
        }),
      );

      // Reset form to mark as clean after successful save
      reset(formValues);
      setSuccessMessage(t("userSettings.messages.settingsUpdated"));
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      // Error is automatically dispatched by API interceptor
      console.error("Settings update failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Notification types that are mandatory and cannot be disabled by the user
  const MANDATORY_NOTIFICATIONS = new Set([NotificationEnum.APPOINTMENTS]);

  const tabs = [
    {
      id: "password" as const,
      label: t("userSettings.tabs.password.label"),
      icon: Lock,
      description: t("userSettings.tabs.password.description"),
    },
    {
      id: "notifications" as const,
      label: t("userSettings.tabs.notifications.label"),
      icon: Bell,
      description: t("userSettings.tabs.notifications.description"),
    },
  ];

  return (
    <div className="p-6 space-y-6 animate-fadeIn">
      {/* Header */}
      <div
        className={`transition-all duration-700 ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
      >
        <div className="flex items-center mb-4 space-x-4">
          <div className="flex justify-center items-center w-12 h-12 rounded-xl bg-ews-primary-100">
            <SettingsIcon className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">
              {t("userSettings.title")}
            </h1>
            <p className="text-neutral-600">{t("userSettings.subtitle")}</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      {successMessage && (
        <div
          className={`p-4 rounded-xl border border-success-200 bg-success-50 transition-all duration-500 ${animateCards
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-4"
            }`}
          style={{ transitionDelay: "200ms" }}
        >
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-success-600" />
            <span className="text-sm font-medium text-success-800">
              {successMessage}
            </span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div
        className={`bg-white rounded-xl shadow-soft border border-neutral-200 transition-all duration-700 ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        style={{ transitionDelay: "300ms" }}
      >
        {/* Tab Navigation */}
        <div className="overflow-hidden rounded-t-xl border-b bg-neutral-50 border-neutral-200">
          <div className="flex flex-wrap">
            {tabs.map((tab, index) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 px-6 py-4 text-left transition-all duration-300 ${animateCards
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-4"
                    } ${activeTab === tab.id
                      ? "bg-white border-b-2 border-primary-500 text-primary-700 shadow-sm"
                      : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100"
                    }`}
                  style={{ transitionDelay: `${400 + index * 100}ms` }}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${activeTab === tab.id
                          ? "bg-primary-500"
                          : "bg-neutral-200"
                        }`}
                    >
                      <IconComponent
                        className={`w-4 h-4 ${activeTab === tab.id
                            ? "text-white"
                            : "text-neutral-600"
                          }`}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{tab.label}</p>
                      <p className="hidden text-xs text-neutral-500 sm:block">
                        {tab.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-8">
          {activeTab === "password" && (
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div className="flex items-center mb-6 space-x-3">
                <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-ews-primary-100">
                  <Lock className="w-4 h-4 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900">
                  {t("userSettings.password.title")}
                </h3>
              </div>

              <div className="space-y-4">
                <Input
                  type="password"
                  leftIcon={<Lock size={16} />}
                  showPasswordToggle
                  label={t("userSettings.password.oldPassword")}
                  name="oldPassword"
                  value={passwordForm.oldPassword}
                  onChange={handlePasswordChange}
                  placeholder={t(
                    "userSettings.password.currentPasswordPlaceholder",
                  )}
                  className="pr-10 transition-all duration-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  error={passwordErrors.oldPassword || undefined}
                />

                <div className="space-y-1">
                  <Input
                    leftIcon={<Lock size={16} />}
                    showPasswordToggle
                    type="password"
                    label={t("userSettings.password.newPassword")}
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    placeholder={t(
                      "userSettings.password.newPasswordPlaceholder",
                    )}
                    className="pr-10 transition-all duration-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    error={passwordErrors.newPassword || undefined}
                  />
                  <p className="text-xs text-neutral-500">
                    {t("userSettings.password.passwordHelp")}
                  </p>
                </div>

                <Input
                  type="password"
                  leftIcon={<Lock size={16} />}
                  showPasswordToggle
                  label={t("userSettings.password.confirmPassword")}
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder={t(
                    "userSettings.password.confirmPasswordPlaceholder",
                  )}
                  className="pr-10 transition-all duration-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  error={passwordErrors.confirmPassword || undefined}
                />
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={!isPasswordFormValid || isLoading}
                  className="flex items-center hover-scale"
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
                      <span>{t("userSettings.password.updating")}</span>
                    </div>
                  ) : (
                    <>
                      <Save className="mr-2 w-4 h-4" />
                      {t("userSettings.password.updateButton")}
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}

          {activeTab === "password" && (
            <ChangePinSection
              onChanged={() => {
                setSuccessMessage(t("userSettings.pin.updated"));
                setTimeout(() => setSuccessMessage(""), 3000);
              }}
            />
          )}

          {activeTab === "password" && (
            <PinLockDelaySection
              onChanged={() => {
                setSuccessMessage(t("userSettings.pinLockDelay.updated"));
                setTimeout(() => setSuccessMessage(""), 3000);
              }}
            />
          )}

          {activeTab === "notifications" && (
            <form
              onSubmit={handleNotificationsFormSubmit(
                handleNotificationsSubmit,
              )}
              className="space-y-6"
            >
              <div className="flex items-center mb-6 space-x-3">
                <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-ews-secondary-100">
                  <Bell className="w-4 h-4 text-secondary-600" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900">
                  {t("userSettings.notifications.title")}
                </h3>
              </div>

              <div className="space-y-4">
                {[NotificationEnum.APPOINTMENTS].map((notificationType) => {
                  const isMandatory = MANDATORY_NOTIFICATIONS.has(notificationType);
                  return (
                    <Controller
                      key={notificationType}
                      name="notifications"
                      control={control}
                      render={({ field }) => {
                        const isEnabled =
                          isMandatory || field.value.includes(notificationType);
                        return (
                          <div className="flex justify-between items-center p-4 rounded-lg bg-neutral-50">
                            <div>
                              <div className="flex items-center space-x-2">
                                <h4 className="text-sm font-medium text-neutral-900">
                                  {t(
                                    `userSettings.notifications.types.${notificationType.toLowerCase()}.title`,
                                  )}
                                </h4>
                                {isMandatory && (
                                  <span className="px-2 py-0.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-full">
                                    {t("userSettings.notifications.required")}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-neutral-500">
                                {t(
                                  `userSettings.notifications.types.${notificationType.toLowerCase()}.description`,
                                )}
                              </p>
                            </div>
                            <button
                              type="button"
                              disabled={isMandatory}
                              onClick={
                                isMandatory
                                  ? undefined
                                  : () => {
                                    const currentNotifications = field.value;
                                    const newNotifications = isEnabled
                                      ? currentNotifications.filter(
                                        (type) => type !== notificationType,
                                      )
                                      : [...currentNotifications, notificationType];
                                    field.onChange(newNotifications);
                                  }
                              }
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isEnabled ? "bg-primary-500" : "bg-neutral-300"
                                } ${isMandatory ? "cursor-not-allowed opacity-75" : "cursor-pointer"}`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isEnabled ? "translate-x-6" : "translate-x-1"
                                  }`}
                              />
                            </button>
                          </div>
                        );
                      }}
                    />
                  );
                })}
              </div>

              {[NotificationEnum.APPOINTMENTS].some(
                (type) => !MANDATORY_NOTIFICATIONS.has(type),
              ) && (
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={!isDirty || isLoading}
                      className="flex items-center hover-scale"
                    >
                      {isLoading ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
                          <span>{t("userSettings.notifications.saving")}</span>
                        </div>
                      ) : (
                        <>
                          <Save className="mr-2 w-4 h-4" />
                          {t("userSettings.notifications.saveButton")}
                        </>
                      )}
                    </Button>
                  </div>
                )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
